import React, { useRef, useState, useEffect, useCallback } from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import { GatsbyImage } from "gatsby-plugin-image";
import { PrismicRichText } from "@prismicio/react";
import { motion, AnimatePresence } from "framer-motion";

// Layout
import { Page } from "../components/containers/page";

// SEO
import { PageSeo } from "../components/seo/page-seo";

// Previews
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";

const SlideshowContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  & .carousel-area {
    position: relative;
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    overflow: hidden;
  }

  & .slide-frame {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 19px 0 3px 0;
  }

  & .gatsby-image-wrapper {
    width: 100%;
    height: 100%;

    & [data-main-image],
    & img {
      width: 100% !important;
      height: 100% !important;
      object-fit: contain !important;
      object-position: center center !important;
    }
  }
`;

const NextButton = styled.button`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50%;
  z-index: 10;
  background: transparent;
  border: none;
  padding: 0;

  &.previous {
    left: 0;

    cursor: url("/icons/previous@1x.png") 0 0, url("/icons/previous.cur") 0 0,
      w-resize !important;
    cursor: -webkit-image-set(
          url("/icons/previous@1x.png") 1x,
          url("/icons/previous@2x.png") 2x
        )
        0 0,
      w-resize !important;
  }

  &.next {
    right: 0;

    cursor: url("/icons/next@1x.png") 0 0, url("/icons/next.cur") 0 0, e-resize !important;
    cursor: -webkit-image-set(
          url("/icons/next@1x.png") 1x,
          url("/icons/next@2x.png") 2x
        )
        0 0,
      e-resize !important;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  flex: 0 0 auto;
  position: relative;
  z-index: 2;
  background: #fff;

  padding: 12px 0;
  /* margin-top: 22px; */

  & .info-left {
    display: block;
    flex: 1;
    text-align: left;
    white-space: normal;
    min-width: 0;

    & h1,
    & p {
      font-size: 17px;
      line-height: 20px;
      letter-spacing: 0.15px;
      margin: 0;
    }

    & h1,
    & p,
    & span,
    & em {
      display: inline;
    }
  }

  & .info-right {
    flex-shrink: 0;
    margin-left: 20px;

    & p {
      font-size: 17px;
      line-height: 20px;
      letter-spacing: 0.15px;
      margin: 0;
      text-align: right;
    }
  }

  @media (max-width: 700px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    column-gap: 12px;

    margin: 0;

    & .info-left {
      display: block;
      min-width: 0;
      white-space: normal;
      padding-right: 6px;

      & h1,
      & p {
        font-size: 15px;
        line-height: 19px;
      }

      & h1,
      & p,
      & span {
        display: inline;
      }
    }

    & .info-right {
      width: auto;
      min-width: max-content;
      white-space: nowrap;
      margin-left: 0;
      margin-top: 0;

      & p {
        font-size: 15px;
        line-height: 19px;
        text-align: right;
      }
    }
  }
`;

const OverviewContent = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  min-height: 0;
  height: 100%;
`;

const fadeTransition = {
  duration: 2,
  ease: [0.33, 1, 0.68, 1],
};

const Index = ({ data, location }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const infoRef = useRef(null);
  const touchStart = useRef(null);

  const carouselItems = (data.prismicOverview?.data?.carousel || []).filter(
    (item) => item.image && item.image.gatsbyImageData
  );

  useEffect(() => {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, []);

  const goNext = useCallback(() => {
    if (carouselItems.length > 1) {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }
  }, [carouselItems.length]);

  const goBack = useCallback(() => {
    if (carouselItems.length > 1) {
      setCurrentSlide(
        (prev) => (prev - 1 + carouselItems.length) % carouselItems.length
      );
    }
  }, [carouselItems.length]);

  const handleTouchStart = useCallback((e) => {
    touchStart.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e) => {
      if (touchStart.current === null) return;
      const diff = touchStart.current - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) goNext();
        else goBack();
      }
      touchStart.current = null;
    },
    [goNext, goBack]
  );

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goBack();
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [goNext, goBack]);

  if (carouselItems.length === 0) {
    return (
      <>
        <PageSeo seoTitle={`Overview`} seoImage={null} seoText={null} />
        <Page noScroll>
          <div className="page-grid">
            <div className="spacer" />
            <div className="content-container">
              <p>No works available.</p>
            </div>
            <div className="spacer" />
          </div>
        </Page>
      </>
    );
  }

  const currentItem = carouselItems[currentSlide];

  return (
    <>
      <PageSeo seoTitle={`Overview`} seoImage={null} seoText={null} />
      <Page noScroll className="overview-page">
        <div className="page-grid">
          <div className="spacer" />

          <div className="content-container">
            <OverviewContent>
              <SlideshowContainer>
                {carouselItems.length > 1 && (
                  <>
                    <NextButton
                      type="button"
                      className="previous"
                      onClick={goBack}
                      aria-label="Previous Slide"
                    />
                    <NextButton
                      type="button"
                      className="next"
                      onClick={goNext}
                      aria-label="Next Slide"
                    />
                  </>
                )}

                <div
                  className="carousel-area"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  <AnimatePresence initial={false}>
                    <motion.div
                      key={currentSlide}
                      className="slide-frame"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={fadeTransition}
                    >
                      <GatsbyImage
                        image={currentItem.image.gatsbyImageData}
                        alt={currentItem.image.alt || ""}
                        objectFit="contain"
                        objectPosition="50% 50%"
                        style={{ width: "100%", height: "100%" }}
                        imgStyle={{
                          objectFit: "contain",
                          objectPosition: "center",
                        }}
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </SlideshowContainer>

              <InfoContainer ref={infoRef}>
                <div className="info-left">
                  <PrismicRichText
                    field={currentItem.caption.richText}
                    components={{
                      paragraph: ({ children }) => <span>{children}</span>,
                      em: ({ children }) => <em>{children}</em>,
                    }}
                  />
                </div>
                <div className="info-right">
                  <p>
                    {currentSlide + 1} of {carouselItems.length}
                  </p>
                </div>
              </InfoContainer>
            </OverviewContent>
          </div>

          <div className="spacer" />
        </div>

        <div className="page-bottom-border">
          <div className="border" />
        </div>
      </Page>
    </>
  );
};

export default withPrismicPreview(Index);

export const query = graphql`
  query Overview {
    prismicOverview {
      id
      data {
        carousel {
          image {
            gatsbyImageData(
              layout: FULL_WIDTH
              breakpoints: [1000, 1500, 2000, 3000, 4000]
            )
            alt
          }
          caption {
            richText
          }
        }
      }
    }
  }
`;
